import { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import UserContext from '../../Components/UserContext';
import HttpInterceptor from '../../lib/HttpInterceptor';
import { toast } from 'react-toastify';
import {
  FaCalendarAlt,
  FaClock,
  FaUser,
  FaCheckCircle,
  FaTimesCircle,
  FaHourglassHalf,
  FaFilter,
  FaChevronLeft,
  FaChevronRight,
  FaExchangeAlt,
  FaTrash
} from 'react-icons/fa';
import ConfirmDialog from '../../Components/ConfirmDialog';
function Appointments() {
  const navigate = useNavigate();
  const { session: trainer } = useContext(UserContext);
  const [appointments, setAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedService, setSelectedService] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [showCounterOfferModal, setShowCounterOfferModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [counterOfferData, setCounterOfferData] = useState({
    date: '',
    time: '',
    slotDuration: '',
    notes: '',
    meetingLink: ''
  });
  const [confirmConfig, setConfirmConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => { },
    variant: 'primary'
  });

  const trainerServices = (trainer?.services && trainer.services.length > 0) ? trainer.services : (trainer?.service ? [trainer.service] : []);
  const servicePartnerTypes = trainer?.servicePartnerTypes || {};

  // Get active service from localStorage
  const activeService = localStorage.getItem('activeService') || trainerServices[0] || '';

  // Filter services to only consultant services
  const consultantServices = trainerServices.filter(service =>
    (servicePartnerTypes[service] || 'instructor') === 'consultant'
  );

  const fetchAppointments = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (selectedService) {
        params.append('service', selectedService);
      }
      if (selectedDate) {
        params.append('startDate', selectedDate);
        const endDate = new Date(selectedDate);
        endDate.setDate(endDate.getDate() + 1);
        params.append('endDate', endDate.toISOString().split('T')[0]);
      }

      const response = await HttpInterceptor.get(`/api/appointments?${params.toString()}`);
      if (response.status === 200) {
        setAppointments(response.data.data || []);
        setFilteredAppointments(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
      toast.error('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, selectedService, selectedDate]);

  useEffect(() => {
    // Redirect if no consultant services
    if (consultantServices.length === 0) {
      toast.error('You don\'t have any consultant services. Appointments are only available for consultant services.');
      navigate('/trainer/classes');
      return;
    }

    fetchAppointments();
  }, [consultantServices.length, fetchAppointments, navigate]);

  const handleApprove = async (appointmentId) => {
    try {
      const meetingLink = prompt('Please paste the Google Meet / Zoom link for this appointment:');
      if (meetingLink === null) return; // User cancelled prompt
      if (!meetingLink.trim()) {
        toast.error('A meeting link is required to approve the appointment.');
        return;
      }

      const response = await HttpInterceptor.post(`/api/appointments/${appointmentId}/approve`, {
        meetingLink: meetingLink.trim()
      });
      if (response.status === 200) {
        toast.success('Appointment approved successfully');
        fetchAppointments();
      }
    } catch (error) {
      console.error('Error approving appointment:', error);
      toast.error(error.response?.data?.message || 'Failed to approve appointment');
    }
  };

  const handleReject = async (appointmentId, reason) => {
    try {
      const rejectionReason = reason !== undefined ? reason : prompt('Reason for rejection (optional):');
      if (rejectionReason === null && reason === undefined) return; // User cancelled prompt

      await HttpInterceptor.post(`/api/appointments/${appointmentId}/reject`, { reason: rejectionReason || '' });
      toast.success('Appointment rejected');
      fetchAppointments();
    } catch (error) {
      console.error('Error rejecting appointment:', error);
      toast.error(error.response?.data?.message || 'Failed to reject appointment');
    }
  };

  const handleCounterOffer = (appointment) => {
    setSelectedAppointment(appointment);
    setCounterOfferData({
      date: '',
      time: '',
      slotDuration: appointment.slotDuration || '',
      notes: '',
      meetingLink: ''
    });
    setShowCounterOfferModal(true);
  };

  const handleCounterOfferSubmit = async () => {
    if (!counterOfferData.date || !counterOfferData.time || !counterOfferData.slotDuration) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const counterOfferDateTime = new Date(`${counterOfferData.date}T${counterOfferData.time}`);

      const response = await HttpInterceptor.post(
        `/api/appointments/${selectedAppointment._id}/counter-offer`,
        {
          counterOfferDateTime: counterOfferDateTime.toISOString(),
          counterOfferSlotDuration: counterOfferData.slotDuration,
          counterOfferNotes: counterOfferData.notes,
          meetingLink: counterOfferData.meetingLink
        }
      );

      if (response.status === 200) {
        toast.success('Counter-offer sent successfully');
        setShowCounterOfferModal(false);
        setSelectedAppointment(null);
        setCounterOfferData({ date: '', time: '', slotDuration: '', notes: '', meetingLink: '' });
        fetchAppointments();
      }
    } catch (error) {
      console.error('Error sending counter-offer:', error);
      toast.error(error.response?.data?.message || 'Failed to send counter-offer');
    }
  };

  const handleRemoveAppointment = (appointmentId) => {
    setConfirmConfig({
      isOpen: true,
      title: 'Remove Appointment',
      message: 'Are you sure you want to remove this appointment from your view? This action cannot be undone.',
      variant: 'danger',
      onConfirm: async () => {
        try {
          const response = await HttpInterceptor.delete(`/api/appointments/${appointmentId}/hide`);
          if (response.status === 200) {
            toast.success('Appointment removed');
            fetchAppointments();
          }
        } catch (error) {
          console.error('Error removing appointment:', error);
          toast.error(error.response?.data?.message || 'Failed to remove appointment');
        }
      }
    });
  };

  const formatDateTime = (dateTime) => {
    const date = new Date(dateTime);
    return {
      date: date.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }),
      time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
    };
  };

  const getStatusBadge = (status, dateTime) => {
    const isPast = dateTime && new Date(dateTime) < new Date();
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: FaHourglassHalf, label: 'Pending' },
      counter_offered: { color: 'bg-orange-100 text-orange-800', icon: FaExchangeAlt, label: 'Alternative Slot Proposed' },
      approved: { color: 'bg-blue-100 text-blue-800', icon: FaCheckCircle, label: 'Approved' },
      rejected: { color: 'bg-red-100 text-red-800', icon: FaTimesCircle, label: 'Rejected' },
      confirmed: { color: 'bg-green-100 text-green-800', icon: FaCheckCircle, label: 'Confirmed' },
      cancelled: { color: 'bg-gray-100 text-gray-800', icon: FaTimesCircle, label: 'Cancelled' },
      completed: { color: 'bg-purple-100 text-purple-800', icon: FaCheckCircle, label: 'Completed' }
    };

    let config = statusConfig[status] || statusConfig.pending;

    if (isPast && (status === 'confirmed' || status === 'approved')) {
      config = { color: 'bg-gray-200 text-gray-700', icon: FaClock, label: 'Expired' };
    }

    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="mr-1" />
        {config.label}
      </span>
    );
  };

  const now = new Date();
  const pendingAppointments = filteredAppointments.filter(apt => 
    apt.status === 'pending' && 
    new Date(apt.requestedDateTime) >= now
  );
  const counterOfferedAppointments = filteredAppointments.filter(apt => 
    apt.status === 'counter_offered' && 
    new Date(apt.counterOfferDateTime || apt.requestedDateTime) >= now
  );
  const approvedAppointments = filteredAppointments.filter(apt => 
    (apt.status === 'approved' || apt.status === 'confirmed') && 
    new Date(apt.requestedDateTime) >= now
  );
  const otherAppointments = filteredAppointments.filter(apt => 
    !['pending', 'counter_offered', 'approved', 'confirmed'].includes(apt.status) ||
    (new Date(apt.counterOfferDateTime || apt.requestedDateTime) < now)
  );

  if (consultantServices.length === 0) {
    return null; // Will redirect
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Appointments</h1>
          <p className="mt-2 text-gray-600">Manage your appointment requests and bookings</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  fetchAppointments();
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="counter_offered">Alternative Slot Proposed</option>
                <option value="approved">Approved</option>
                <option value="confirmed">Confirmed</option>
                <option value="completed">Completed</option>
                <option value="rejected">Rejected</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            {consultantServices.length > 1 && (
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">Service</label>
                <select
                  value={selectedService}
                  onChange={(e) => setSelectedService(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                >
                  <option value="">All Services</option>
                  {consultantServices.map((service) => (
                    <option key={service} value={service}>{service}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>

            <button
              onClick={() => {
                setStatusFilter('all');
                setSelectedService('');
                setSelectedDate('');
              }}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
            <p className="mt-2 text-gray-600">Loading appointments...</p>
          </div>
        ) : (
          <>
            {/* Pending Appointments */}
            {pendingAppointments.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <FaHourglassHalf className="mr-2 text-yellow-600" />
                  Pending Approval ({pendingAppointments.length})
                </h2>
                <div className="space-y-4">
                  {pendingAppointments.map((appointment) => {
                    const { date, time } = formatDateTime(appointment.requestedDateTime);
                    return (
                      <div key={appointment._id} className="bg-white rounded-lg shadow p-6 border-l-4 border-yellow-500">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <h3 className="text-lg font-semibold text-gray-900">
                                {(appointment.program?.name || 'Program')}
                              </h3>
                              {getStatusBadge(appointment.status, appointment.requestedDateTime)}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                              <div className="flex items-center">
                                <FaUser className="mr-2" />
                                <span>{(appointment.member?.name || 'Member')}</span>
                              </div>
                              <div className="flex items-center">
                                <FaCalendarAlt className="mr-2" />
                                <span>{date}</span>
                              </div>
                              <div className="flex items-center">
                                <FaClock className="mr-2" />
                                <span>{time} ({appointment.slotDuration})</span>
                              </div>
                              <div className="flex items-center">
                                <span className="font-medium">Fee: ₹{appointment.feeAmount}</span>
                              </div>
                            </div>
                            {appointment.memberNotes && (
                              <div className="mt-3 p-3 bg-gray-50 rounded text-sm text-gray-700">
                                <strong>Notes:</strong> {appointment.memberNotes}
                              </div>
                            )}
                          </div>
                          <div className="ml-4 flex flex-col gap-2">
                            <button
                              onClick={() => handleApprove(appointment._id)}
                              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => handleCounterOffer(appointment)}
                              className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition flex items-center gap-2"
                            >
                              <FaExchangeAlt />
                              Alternative Slot
                            </button>
                            <button
                              onClick={() => {
                                const reason = prompt('Reason for rejection (optional):');
                                if (reason !== null) {
                                  handleReject(appointment._id, reason);
                                }
                              }}
                              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition"
                            >
                              Reject
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Alternative Slot Proposed Appointments */}
            {counterOfferedAppointments.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <FaExchangeAlt className="mr-2 text-orange-600" />
                  Alternative Slot Proposed ({counterOfferedAppointments.length})
                </h2>
                <div className="space-y-4">
                  {counterOfferedAppointments.map((appointment) => {
                    const { date, time } = formatDateTime(appointment.requestedDateTime);
                    const counterOfferDate = appointment.counterOfferDateTime
                      ? formatDateTime(appointment.counterOfferDateTime)
                      : null;
                    return (
                      <div key={appointment._id} className="bg-white rounded-lg shadow p-6 border-l-4 border-orange-500">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <h3 className="text-lg font-semibold text-gray-900">
                                {(appointment.program?.name || 'Program')}
                              </h3>
                              {getStatusBadge(appointment.status, appointment.counterOfferDateTime || appointment.requestedDateTime)}
                            </div>
                            <div className="mb-3 p-3 bg-gray-50 rounded">
                              <p className="text-sm font-medium text-gray-700 mb-2">Original Request:</p>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                                <div className="flex items-center">
                                  <FaCalendarAlt className="mr-2" />
                                  <span>{date}</span>
                                </div>
                                <div className="flex items-center">
                                  <FaClock className="mr-2" />
                                  <span>{time} ({appointment.slotDuration})</span>
                                </div>
                              </div>
                            </div>
                            {counterOfferDate && (
                              <div className="mb-3 p-3 bg-orange-50 rounded border border-orange-200">
                                <p className="text-sm font-medium text-orange-800 mb-2">Your Alternative Slot:</p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-orange-700">
                                  <div className="flex items-center">
                                    <FaCalendarAlt className="mr-2" />
                                    <span>{counterOfferDate.date}</span>
                                  </div>
                                  <div className="flex items-center">
                                    <FaClock className="mr-2" />
                                    <span>{counterOfferDate.time} ({appointment.counterOfferSlotDuration})</span>
                                  </div>
                                </div>
                                {appointment.counterOfferNotes && (
                                  <div className="mt-2 text-sm text-orange-700">
                                    <strong>Notes:</strong> {appointment.counterOfferNotes}
                                  </div>
                                )}
                              </div>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                              <div className="flex items-center">
                                <FaUser className="mr-2" />
                                <span>{(appointment.member?.name || 'Member')}</span>
                              </div>
                              <div className="flex items-center">
                                <span className="font-medium">Fee: ₹{appointment.feeAmount}</span>
                              </div>
                            </div>
                            {appointment.memberNotes && (
                              <div className="mt-3 p-3 bg-gray-50 rounded text-sm text-gray-700">
                                <strong>Member Notes:</strong> {appointment.memberNotes}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Approved/Confirmed Appointments */}
            {approvedAppointments.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <FaCheckCircle className="mr-2 text-green-600" />
                  Upcoming Appointments ({approvedAppointments.length})
                </h2>
                <div className="space-y-4">
                  {approvedAppointments.map((appointment) => {
                    const { date, time } = formatDateTime(appointment.requestedDateTime);
                    return (
                      <div key={appointment._id} className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <h3 className="text-lg font-semibold text-gray-900">
                                {(appointment.program?.name || 'Program')}
                              </h3>
                               {getStatusBadge(appointment.status, appointment.requestedDateTime)}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                              <div className="flex items-center">
                                <FaUser className="mr-2" />
                                <span>{(appointment.member?.name || 'Member')}</span>
                              </div>
                              <div className="flex items-center">
                                <FaCalendarAlt className="mr-2" />
                                <span>{date}</span>
                              </div>
                              <div className="flex items-center">
                                <FaClock className="mr-2" />
                                <span>{time} ({appointment.slotDuration})</span>
                              </div>
                              <div className="flex items-center">
                                <span className="font-medium">
                                  Fee: ₹{appointment.feeAmount}
                                  {appointment.paymentStatus === 'paid' && (
                                    <span className="ml-2 text-green-600">(Paid)</span>
                                  )}
                                </span>
                              </div>
                            </div>
                            {appointment.meetingLink && (
                              <div className="mt-3">
                                <a
                                  href={appointment.meetingLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-cyan-600 hover:text-cyan-700 font-medium"
                                >
                                  Join Meeting →
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Other Appointments */}
            {otherAppointments.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Past & History ({otherAppointments.length})</h2>
                <div className="space-y-4">
                  {otherAppointments.map((appointment) => {
                    const { date, time } = formatDateTime(appointment.requestedDateTime);
                    return (
                      <div key={appointment._id} className="bg-white rounded-lg shadow p-6">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <h3 className="text-lg font-semibold text-gray-900">
                                {(appointment.program?.name || 'Program')}
                              </h3>
                              {getStatusBadge(appointment.status, appointment.requestedDateTime)}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                              <div className="flex items-center">
                                <FaUser className="mr-2" />
                                <span>{(appointment.member?.name || 'Member')}</span>
                              </div>
                              <div className="flex items-center">
                                <FaCalendarAlt className="mr-2" />
                                <span>{date}</span>
                              </div>
                              <div className="flex items-center">
                                <FaClock className="mr-2" />
                                <span>{time} ({appointment.slotDuration})</span>
                              </div>
                            </div>
                            {appointment.reason && (
                              <div className="mt-3 p-3 bg-gray-50 rounded text-sm text-gray-700">
                                <strong>Reason:</strong> {appointment.reason}
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <button
                              onClick={() => handleRemoveAppointment(appointment._id)}
                              className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                              title="Remove"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {filteredAppointments.length === 0 && (
              <div className="text-center py-12 bg-white rounded-lg shadow">
                <FaCalendarAlt className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-4 text-lg font-medium text-gray-900">No appointments found</h3>
                <p className="mt-2 text-gray-600">
                  {statusFilter !== 'all' || selectedService || selectedDate
                    ? 'Try adjusting your filters'
                    : 'You don\'t have any appointments yet'}
                </p>
              </div>
            )}
          </>
        )}

        {/* Alternative Slot Modal */}
        {showCounterOfferModal && selectedAppointment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Propose Alternative Slot</h2>
              <p className="text-sm text-gray-600 mb-4">
                Suggest an alternative date and time for this appointment.
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={counterOfferData.date}
                    onChange={(e) => setCounterOfferData({ ...counterOfferData, date: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="time"
                    value={counterOfferData.time}
                    onChange={(e) => setCounterOfferData({ ...counterOfferData, time: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Slot Duration <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={counterOfferData.slotDuration}
                    onChange={(e) => setCounterOfferData({ ...counterOfferData, slotDuration: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    required
                  >
                    <option value="">Select duration</option>
                    {selectedAppointment.program?.slotDurations?.map((duration) => (
                      <option key={duration} value={duration}>{duration}</option>
                    )) || ['30 mins', '45 mins', '1 hour', '1.5 hours', '2 hours', '3 hours'].map((duration) => (
                      <option key={duration} value={duration}>{duration}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Meeting Link (Google Meet / Zoom) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="url"
                    value={counterOfferData.meetingLink}
                    onChange={(e) => setCounterOfferData({ ...counterOfferData, meetingLink: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    placeholder="https://meet.google.com/..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={counterOfferData.notes}
                    onChange={(e) => setCounterOfferData({ ...counterOfferData, notes: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    placeholder="Add any notes for the member..."
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleCounterOfferSubmit}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition"
                >
                  Send Alternative Slot
                </button>
                <button
                  onClick={() => {
                    setShowCounterOfferModal(false);
                    setSelectedAppointment(null);
                    setCounterOfferData({ date: '', time: '', slotDuration: '', notes: '', meetingLink: '' });
                  }}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Unified Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmConfig.isOpen}
        onClose={() => setConfirmConfig({ ...confirmConfig, isOpen: false })}
        onConfirm={confirmConfig.onConfirm}
        title={confirmConfig.title}
        message={confirmConfig.message}
        variant={confirmConfig.variant}
      />
    </div>
  );
}

export default Appointments;


